import { useState } from 'react';

import { ChevronDownIcon } from '../../../assets/icons/index';
import { Order } from '../../../types/general.types';
import Button from '../../button/index';
import { TableHeaderRowData } from '../table';

type SortState<TColumn extends string> = {
  field: null | TColumn;
  order: null | Order;
};

export type SortStateCallback<TColumn extends string> = (state: SortState<TColumn>) => void;

export const useTableSort = <TColumn extends string>(
  columnsToSort: TColumn[],
  columnTranslationKey: Record<TColumn, string>,
  callback?: SortStateCallback<TColumn>,
) => {
  const [sort, setSort] = useState<SortState<TColumn>>({
    field: null,
    order: null,
  });

  const handleSort = (field: TColumn) =>
    setSort((prev) => {
      let newState: SortState<TColumn> = { field, order: Order.ASC };

      if (prev.field !== field) {
        newState = { field, order: Order.ASC };
      }
      if (prev.order === Order.ASC) {
        newState = { field, order: Order.DESC };
      }
      if (prev.order === Order.DESC) {
        newState = { field: null, order: null };
      }
      callback?.(newState);
      return newState;
    });

  const columnsTitle: TableHeaderRowData<TColumn> = Object.fromEntries(
    Object.entries(columnTranslationKey).map(([key, value]) => {
      if (!columnsToSort.includes(key)) return [key, value];

      const isCurrentColumnSorted = sort.field === key;

      return [
        key,
        <div className="flex w-fit items-center gap-1" onClick={() => handleSort(key)}>
          <span className="text-label-md font-[450] text-[#172335]">{value}</span>
          <Button
            className="relative flex size-4 justify-center rounded hover:bg-gray-200"
            styleType="NONE"
          >
            {isCurrentColumnSorted && sort.order === Order.DESC ? (
              <ChevronDownIcon className="w-2 rotate-180" />
            ) : isCurrentColumnSorted && sort.order === Order.ASC ? (
              <ChevronDownIcon className="w-2" />
            ) : (
              <>
                <ChevronDownIcon className="absolute -top-[3px] w-2 rotate-180" />
                <ChevronDownIcon className="absolute -bottom-[3px] w-2" />
              </>
            )}
          </Button>
        </div>,
      ];
    }),
  );

  return [columnsTitle, sort] as const;
};
